import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Mic, MicOff, Video, VideoOff, Phone, PhoneOff, 
  Monitor, MonitorOff, MessageSquare, Users, Settings,
  Copy, Check, X, Send, Hand, Maximize, Minimize,
  PictureInPicture2, Signal, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { BACKEND_URL } from '@/config/api';

// Call Quality Indicator Component
const CallQualityIndicator = ({ stats }) => {
  const getQualityLevel = () => {
    if (!stats) return 'unknown';
    const { bitrate, latency, packetsLost } = stats;
    
    if (latency > 300 || packetsLost > 10) return 'poor';
    if (latency > 150 || packetsLost > 5 || bitrate < 500) return 'fair';
    return 'good';
  };
  
  const quality = getQualityLevel();
  const colors = {
    good: 'text-green-500',
    fair: 'text-yellow-500',
    poor: 'text-red-500',
    unknown: 'text-gray-500'
  };
  
  return (
    <div className="flex items-center gap-2 text-xs">
      <Signal size={14} className={colors[quality]} />
      <span className="text-gray-400">
        {stats?.bitrate || 0} kbps • {stats?.latency || 0}ms
      </span>
    </div>
  );
};

// Participant Video Component with PiP support
const ParticipantVideo = ({ 
  participant, 
  stream, 
  isLocal, 
  onPiP,
  handRaised 
}) => {
  const videoRef = useRef(null);
  
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);
  
  const handlePiP = async () => {
    if (videoRef.current && document.pictureInPictureEnabled) {
      try {
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture();
        } else {
          await videoRef.current.requestPictureInPicture();
        }
        onPiP?.();
      } catch (err) {
        console.error('PiP failed:', err);
      }
    }
  };
  
  return (
    <div className="relative bg-gray-800 rounded-xl overflow-hidden aspect-video group">
      <video
        ref={videoRef}
        autoPlay
        muted={isLocal}
        playsInline
        className="w-full h-full object-cover"
      />
      
      {/* Hand raised indicator */}
      {handRaised && (
        <div className="absolute top-2 right-2 bg-yellow-500 rounded-full p-1.5 animate-bounce">
          <Hand size={16} className="text-white" />
        </div>
      )}
      
      {/* Name badge */}
      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-white text-sm flex items-center gap-1">
        {isLocal ? 'You' : participant?.name || 'Participant'}
        {participant?.audio_enabled === false && <MicOff size={12} />}
      </div>
      
      {/* PiP button (on hover) */}
      <button
        onClick={handlePiP}
        className="absolute top-2 left-2 p-1.5 bg-black/50 rounded opacity-0 group-hover:opacity-100 transition-opacity text-white hover:bg-black/70"
        title="Picture-in-Picture"
      >
        <PictureInPicture2 size={14} />
      </button>
    </div>
  );
};

export default function MeetingRoom() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  // Media state
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const [isHandRaised, setIsHandRaised] = useState(false);
  
  // UI state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showStats, setShowStats] = useState(false);
  
  // Meeting state
  const [meeting, setMeeting] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [isConnecting, setIsConnecting] = useState(true);
  const [error, setError] = useState(null);
  
  // Call quality stats
  const [callStats, setCallStats] = useState({
    bitrate: 0,
    packetsLost: 0,
    latency: 0,
    frameRate: 0
  });
  
  // WebRTC refs
  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef({});
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const peerConnectionsRef = useRef({});
  const wsRef = useRef(null);
  const handleSignalingMessageRef = useRef(null);
  const containerRef = useRef(null);
  
  // ICE servers configuration
  const iceServers = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:openrelay.metered.ca:80' },
      {
        urls: 'turn:openrelay.metered.ca:80',
        username: 'openrelayproject',
        credential: 'openrelayproject'
      }
    ]
  };

  // Fetch meeting details
  useEffect(() => {
    const fetchMeeting = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/meetings/${meetingId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setMeeting(data);
          setParticipants(data.participants || []);
        } else {
          setError('Meeting not found');
        }
      } catch (err) {
        setError('Failed to load meeting');
      }
    };
    
    if (meetingId && token) {
      fetchMeeting();
    }
  }, [meetingId, token]);

  // Initialize media and WebSocket
  useEffect(() => {
    let isMounted = true;
    
    const initializeMedia = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });
        
        if (!isMounted) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        
        localStreamRef.current = stream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        
        // Connect to WebSocket signaling server
        const wsUrl = `${BACKEND_URL.replace('http', 'ws')}/api/meetings/ws/${meetingId}`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        ws.onopen = () => {
          ws.send(JSON.stringify({
            type: 'auth',
            user_id: user.id,
            user_info: {
              name: user.name,
              avatar_color: user.avatar_color
            }
          }));
          setIsConnecting(false);
        };
        
        ws.onmessage = async (event) => {
          const data = JSON.parse(event.data);
          if (handleSignalingMessageRef.current) {
            await handleSignalingMessageRef.current(data);
          }
        };
        
        ws.onerror = (err) => {
          console.error('WebSocket error:', err);
          setError('Connection error');
        };
        
        ws.onclose = () => {
          console.log('WebSocket closed');
        };
        
      } catch (err) {
        console.error('Media error:', err);
        setError('Could not access camera/microphone');
        setIsConnecting(false);
      }
    };
    
    if (meeting && user) {
      initializeMedia();
    }
    
    return () => {
      isMounted = false;
      const localStream = localStreamRef.current;
      const screenStream = screenStreamRef.current;
      const ws = wsRef.current;
      const peerConnections = { ...peerConnectionsRef.current };
      
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
      if (screenStream) {
        screenStream.getTracks().forEach(track => track.stop());
      }
      if (ws) {
        ws.close();
      }
      Object.values(peerConnections).forEach(pc => pc.close());
    };
  }, [meeting, user, meetingId]);

  // Call quality monitoring
  useEffect(() => {
    const interval = setInterval(async () => {
      const pcs = Object.values(peerConnectionsRef.current);
      if (pcs.length === 0) return;
      
      let totalBitrate = 0;
      let totalLatency = 0;
      let totalPacketsLost = 0;
      let count = 0;
      
      for (const pc of pcs) {
        try {
          const stats = await pc.getStats();
          stats.forEach(report => {
            if (report.type === 'inbound-rtp' && report.kind === 'video') {
              totalPacketsLost += report.packetsLost || 0;
            }
            if (report.type === 'candidate-pair' && report.state === 'succeeded') {
              totalLatency += (report.currentRoundTripTime || 0) * 1000;
              count++;
            }
          });
        } catch (e) {
          // Ignore stats errors
        }
      }
      
      setCallStats({
        bitrate: Math.round(totalBitrate / Math.max(count, 1)),
        latency: Math.round(totalLatency / Math.max(count, 1)),
        packetsLost: totalPacketsLost,
        frameRate: 30
      });
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  // Handle signaling messages
  const handleSignalingMessage = useCallback(async (data) => {
    switch (data.type) {
      case 'participants':
        for (const participantId of data.participants) {
          if (participantId !== user.id && !peerConnectionsRef.current[participantId]) {
            await createPeerConnection(participantId, true);
          }
        }
        break;
        
      case 'user_joined':
        setParticipants(prev => [...prev, { user_id: data.user_id, ...data.user_info }]);
        break;
        
      case 'user_left':
        if (peerConnectionsRef.current[data.user_id]) {
          peerConnectionsRef.current[data.user_id].close();
          delete peerConnectionsRef.current[data.user_id];
        }
        setParticipants(prev => prev.filter(p => p.user_id !== data.user_id));
        break;
        
      case 'offer':
        await handleOffer(data.from, data.offer);
        break;
        
      case 'answer':
        await handleAnswer(data.from, data.answer);
        break;
        
      case 'ice-candidate':
        await handleIceCandidate(data.from, data.candidate);
        break;
        
      case 'media-state':
        setParticipants(prev => prev.map(p => 
          p.user_id === data.from 
            ? { ...p, audio_enabled: data.audio_enabled, video_enabled: data.video_enabled }
            : p
        ));
        break;
      
      case 'hand-raised':
        setParticipants(prev => prev.map(p => 
          p.user_id === data.user_id 
            ? { ...p, hand_raised: data.raised }
            : p
        ));
        break;
        
      case 'chat':
      case 'chat-message':
        setChatMessages(prev => [...prev, {
          from: data.from || data.user_id,
          user_info: data.user_info,
          message: data.message,
          timestamp: data.timestamp
        }]);
        break;
        
      case 'screen-share-start':
        setParticipants(prev => prev.map(p => 
          p.user_id === data.user_id ? { ...p, is_screen_sharing: true } : p
        ));
        break;
        
      case 'screen-share-stop':
        setParticipants(prev => prev.map(p => 
          p.user_id === data.user_id ? { ...p, is_screen_sharing: false } : p
        ));
        break;
        
      case 'meeting_ended':
        alert('Meeting has ended');
        navigate('/dashboard');
        break;
      default:
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, navigate]);

  useEffect(() => {
    handleSignalingMessageRef.current = handleSignalingMessage;
  }, [handleSignalingMessage]);

  // Create peer connection
  const createPeerConnection = async (peerId, initiator = false) => {
    const pc = new RTCPeerConnection(iceServers);
    peerConnectionsRef.current[peerId] = pc;
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }
    
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current) {
        wsRef.current.send(JSON.stringify({
          type: 'ice-candidate',
          target: peerId,
          candidate: event.candidate
        }));
      }
    };
    
    pc.ontrack = (event) => {
      const remoteVideo = remoteVideoRefs.current[peerId];
      if (remoteVideo) {
        remoteVideo.srcObject = event.streams[0];
      }
    };
    
    if (initiator) {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      wsRef.current?.send(JSON.stringify({
        type: 'offer',
        target: peerId,
        offer: offer
      }));
    }
    
    return pc;
  };

  const handleOffer = async (from, offer) => {
    let pc = peerConnectionsRef.current[from];
    if (!pc) {
      pc = await createPeerConnection(from, false);
    }
    
    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    
    wsRef.current?.send(JSON.stringify({
      type: 'answer',
      target: from,
      answer: answer
    }));
  };

  const handleAnswer = async (from, answer) => {
    const pc = peerConnectionsRef.current[from];
    if (pc) {
      await pc.setRemoteDescription(new RTCSessionDescription(answer));
    }
  };

  const handleIceCandidate = async (from, candidate) => {
    const pc = peerConnectionsRef.current[from];
    if (pc && candidate) {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    }
  };

  // Toggle audio
  const toggleAudio = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioEnabled(audioTrack.enabled);
        
        wsRef.current?.send(JSON.stringify({
          type: 'media-state',
          audio_enabled: audioTrack.enabled,
          video_enabled: isVideoEnabled
        }));
      }
    }
  };

  // Toggle video
  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoEnabled(videoTrack.enabled);
        
        wsRef.current?.send(JSON.stringify({
          type: 'media-state',
          audio_enabled: isAudioEnabled,
          video_enabled: videoTrack.enabled
        }));
      }
    }
  };

  // Toggle screen share
  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
        screenStreamRef.current = null;
      }
      
      if (localStreamRef.current && localVideoRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
        
        // Replace track in peer connections
        const videoTrack = localStreamRef.current.getVideoTracks()[0];
        if (videoTrack) {
          Object.values(peerConnectionsRef.current).forEach(pc => {
            const sender = pc.getSenders().find(s => s.track?.kind === 'video');
            if (sender) {
              sender.replaceTrack(videoTrack);
            }
          });
        }
      }
      
      setIsScreenSharing(false);
      wsRef.current?.send(JSON.stringify({ type: 'screen-share-stop' }));
    } else {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        screenStreamRef.current = screenStream;
        
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = screenStream;
        }
        
        const screenTrack = screenStream.getVideoTracks()[0];
        Object.values(peerConnectionsRef.current).forEach(pc => {
          const sender = pc.getSenders().find(s => s.track?.kind === 'video');
          if (sender) {
            sender.replaceTrack(screenTrack);
          }
        });
        
        screenTrack.onended = () => {
          toggleScreenShare();
        };
        
        setIsScreenSharing(true);
        wsRef.current?.send(JSON.stringify({ type: 'screen-share-start' }));
      } catch (err) {
        console.error('Screen share error:', err);
      }
    }
  };

  // Toggle raise hand
  const toggleRaiseHand = () => {
    const newState = !isHandRaised;
    setIsHandRaised(newState);
    wsRef.current?.send(JSON.stringify({
      type: 'hand-raised',
      raised: newState
    }));
  };

  // Toggle fullscreen
  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Leave meeting
  const leaveMeeting = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/meetings/${meetingId}/leave`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Error leaving meeting:', err);
    }
    navigate('/dashboard');
  };

  // Send chat message
  const sendChatMessage = () => {
    if (!chatInput.trim()) return;
    
    wsRef.current?.send(JSON.stringify({
      type: 'chat-message',
      message: chatInput
    }));
    
    setChatMessages(prev => [...prev, {
      from: user.id,
      user_info: { name: user.name },
      message: chatInput,
      timestamp: new Date().toISOString()
    }]);
    
    setChatInput('');
  };

  // Copy meeting link
  const copyMeetingLink = () => {
    const link = `${window.location.origin}/meeting/join/${meeting?.room_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">{error}</p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  if (isConnecting) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Connecting to meeting...</p>
        </div>
      </div>
    );
  }

  // Minimized floating widget view
  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 w-80 bg-gray-900 rounded-xl shadow-2xl overflow-hidden z-50 border border-gray-700"
        data-testid="meeting-minimized"
      >
        <div className="p-2 bg-gray-800 flex items-center justify-between">
          <span className="text-white text-sm font-medium truncate">{meeting?.name}</span>
          <button
            onClick={() => setIsMinimized(false)}
            className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
          >
            <ChevronUp size={16} />
          </button>
        </div>
        <div className="aspect-video relative">
          <video
            ref={localVideoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover"
          />
        </div>
        <div className="p-2 flex items-center justify-center gap-2">
          <button
            onClick={toggleAudio}
            className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center",
              isAudioEnabled ? "bg-gray-700 text-white" : "bg-red-600 text-white"
            )}
          >
            {isAudioEnabled ? <Mic size={14} /> : <MicOff size={14} />}
          </button>
          <button
            onClick={toggleVideo}
            className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center",
              isVideoEnabled ? "bg-gray-700 text-white" : "bg-red-600 text-white"
            )}
          >
            {isVideoEnabled ? <Video size={14} /> : <VideoOff size={14} />}
          </button>
          <button
            onClick={leaveMeeting}
            className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center"
          >
            <PhoneOff size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-gray-900 flex flex-col" 
      data-testid="meeting-room"
    >
      {/* Header */}
      <div className="h-14 bg-gray-800 border-b border-gray-700 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <h1 className="text-white font-semibold">{meeting?.name || 'Meeting'}</h1>
          <button
            onClick={copyMeetingLink}
            className="flex items-center gap-1 px-2 py-1 bg-gray-700 rounded text-sm text-gray-300 hover:bg-gray-600"
          >
            {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
            {meeting?.room_code}
          </button>
        </div>
        <div className="flex items-center gap-4">
          {showStats && <CallQualityIndicator stats={callStats} />}
          <button
            onClick={() => setShowStats(!showStats)}
            className="text-gray-400 hover:text-white"
            title="Toggle stats"
          >
            <Signal size={18} />
          </button>
          <span className="text-gray-400 text-sm">
            {participants.length} participant{participants.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex">
        {/* Video grid */}
        <div className={cn("flex-1 p-4", (isChatOpen || isParticipantsOpen) && "pr-80")}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 h-full">
            {/* Local video */}
            <ParticipantVideo
              participant={{ name: 'You', audio_enabled: isAudioEnabled }}
              stream={localStreamRef.current}
              isLocal={true}
              handRaised={isHandRaised}
            />
            
            {/* Remote videos */}
            {participants.filter(p => p.user_id !== user?.id).map(participant => (
              <div key={participant.user_id} className="relative bg-gray-800 rounded-xl overflow-hidden aspect-video group">
                <video
                  ref={el => remoteVideoRefs.current[participant.user_id] = el}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
                {participant.hand_raised && (
                  <div className="absolute top-2 right-2 bg-yellow-500 rounded-full p-1.5 animate-bounce">
                    <Hand size={16} className="text-white" />
                  </div>
                )}
                {participant.is_screen_sharing && (
                  <div className="absolute top-2 left-2 px-2 py-1 bg-blue-600 rounded text-xs text-white flex items-center gap-1">
                    <Monitor size={12} />
                    Screen
                  </div>
                )}
                <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-white text-sm flex items-center gap-1">
                  {participant.name || 'Participant'}
                  {!participant.audio_enabled && <MicOff size={12} />}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Side panels */}
        {(isChatOpen || isParticipantsOpen) && (
          <div className="w-80 bg-gray-800 border-l border-gray-700 flex flex-col absolute right-0 top-14 bottom-20">
            <div className="h-12 border-b border-gray-700 flex items-center justify-between px-4">
              <span className="text-white font-medium">
                {isChatOpen ? 'In-call Chat' : 'Participants'}
              </span>
              <button
                onClick={() => { setIsChatOpen(false); setIsParticipantsOpen(false); }}
                className="text-gray-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            {isChatOpen && (
              <>
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  {chatMessages.length === 0 && (
                    <p className="text-gray-500 text-center text-sm">No messages yet</p>
                  )}
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={cn(
                      "max-w-[85%]",
                      msg.from === user?.id ? "ml-auto" : ""
                    )}>
                      <div className={cn(
                        "px-3 py-2 rounded-lg",
                        msg.from === user?.id ? "bg-blue-600 text-white" : "bg-gray-700 text-white"
                      )}>
                        {msg.from !== user?.id && (
                          <p className="text-xs text-gray-400 mb-1">{msg.user_info?.name}</p>
                        )}
                        <p className="text-sm">{msg.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-3 border-t border-gray-700">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && sendChatMessage()}
                      placeholder="Type a message..."
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm"
                      data-testid="chat-input"
                    />
                    <Button size="sm" onClick={sendChatMessage} data-testid="send-chat-btn">
                      <Send size={16} />
                    </Button>
                  </div>
                </div>
              </>
            )}

            {isParticipantsOpen && (
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {/* Current user */}
                <div className="flex items-center gap-3 p-2 bg-gray-700/50 rounded-lg">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                    style={{ backgroundColor: user?.avatar_color || '#6366F1' }}
                  >
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-sm">{user?.name} (You)</p>
                    <p className="text-gray-400 text-xs">Host</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {!isAudioEnabled && <MicOff size={14} className="text-gray-500" />}
                    {!isVideoEnabled && <VideoOff size={14} className="text-gray-500" />}
                    {isHandRaised && <Hand size={14} className="text-yellow-500" />}
                  </div>
                </div>
                
                {/* Other participants */}
                {participants.filter(p => p.user_id !== user?.id).map(participant => (
                  <div key={participant.user_id} className="flex items-center gap-3 p-2 bg-gray-700/50 rounded-lg">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: participant.avatar_color || '#6366F1' }}
                    >
                      {participant.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-sm">{participant.name}</p>
                      <p className="text-gray-400 text-xs">Participant</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {!participant.audio_enabled && <MicOff size={14} className="text-gray-500" />}
                      {!participant.video_enabled && <VideoOff size={14} className="text-gray-500" />}
                      {participant.hand_raised && <Hand size={14} className="text-yellow-500" />}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Controls bar */}
      <div className="h-20 bg-gray-800 border-t border-gray-700 flex items-center justify-center gap-3">
        <button
          onClick={toggleAudio}
          className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center transition-colors",
            isAudioEnabled ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-red-600 hover:bg-red-500 text-white"
          )}
          data-testid="toggle-audio-btn"
          title={isAudioEnabled ? 'Mute' : 'Unmute'}
        >
          {isAudioEnabled ? <Mic size={20} /> : <MicOff size={20} />}
        </button>
        
        <button
          onClick={toggleVideo}
          className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center transition-colors",
            isVideoEnabled ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-red-600 hover:bg-red-500 text-white"
          )}
          data-testid="toggle-video-btn"
          title={isVideoEnabled ? 'Stop video' : 'Start video'}
        >
          {isVideoEnabled ? <Video size={20} /> : <VideoOff size={20} />}
        </button>
        
        <button
          onClick={toggleScreenShare}
          className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center transition-colors",
            isScreenSharing ? "bg-blue-600 hover:bg-blue-500 text-white" : "bg-gray-700 hover:bg-gray-600 text-white"
          )}
          data-testid="toggle-screen-btn"
          title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
        >
          {isScreenSharing ? <Monitor size={20} /> : <MonitorOff size={20} />}
        </button>
        
        <button
          onClick={toggleRaiseHand}
          className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center transition-colors",
            isHandRaised ? "bg-yellow-500 hover:bg-yellow-400 text-white" : "bg-gray-700 hover:bg-gray-600 text-white"
          )}
          data-testid="raise-hand-btn"
          title={isHandRaised ? 'Lower hand' : 'Raise hand'}
        >
          <Hand size={20} />
        </button>
        
        <div className="w-px h-8 bg-gray-600 mx-1" />
        
        <button
          onClick={() => { setIsChatOpen(!isChatOpen); setIsParticipantsOpen(false); }}
          className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center transition-colors",
            isChatOpen ? "bg-blue-600 text-white" : "bg-gray-700 hover:bg-gray-600 text-white"
          )}
          title="Chat"
        >
          <MessageSquare size={20} />
        </button>
        
        <button
          onClick={() => { setIsParticipantsOpen(!isParticipantsOpen); setIsChatOpen(false); }}
          className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center transition-colors",
            isParticipantsOpen ? "bg-blue-600 text-white" : "bg-gray-700 hover:bg-gray-600 text-white"
          )}
          title="Participants"
        >
          <Users size={20} />
        </button>
        
        <div className="w-px h-8 bg-gray-600 mx-1" />
        
        <button
          onClick={() => setIsMinimized(true)}
          className="w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center transition-colors"
          title="Minimize"
        >
          <ChevronDown size={20} />
        </button>
        
        <button
          onClick={toggleFullscreen}
          className="w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center transition-colors"
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
        </button>
        
        <div className="w-px h-8 bg-gray-600 mx-1" />
        
        <button
          onClick={leaveMeeting}
          className="w-12 h-12 rounded-full bg-red-600 hover:bg-red-500 text-white flex items-center justify-center transition-colors"
          data-testid="leave-meeting-btn"
          title="Leave meeting"
        >
          <PhoneOff size={20} />
        </button>
      </div>
    </div>
  );
}
